import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, MailOpen, Reply } from 'lucide-react';
import { useDossierEmails } from '../../hooks/queries/useGmailInbox';
import { useDossierGraftEmails } from '../../hooks/queries/useDossierGraftEmails';
import { useDossier } from '../../hooks/queries/useDossiers';
import { GRAFT_UNIFIED_ADDRESS } from '../../services/dossiers/emailGraft';
import { buildReplyMailto } from '../../services/dossiers/emailReply';
import { DossierEmailViewerModal } from './DossierEmailViewerModal';
import { Badge } from '../ui/Badge';
import { downloadEmailAsEml, type MergedEmail } from '../../services/dossiers/dossierEmailService';
import { canOpenInOutlook, openGraftInOutlook } from '../../services/dossiers/outlookOpen';

import { formatAppDate } from '../../lib/dateFormat';
function EmailBadge({ item }: { item: MergedEmail }) {
  if (item.kind === 'gmail') return <Badge variant="primary">Gmail</Badge>;
  if (item.row.direction === 'inbound_forward') return <Badge variant="success">Transfert</Badge>;
  if (item.row.direction === 'outbound_addin') return <Badge variant="primary">Envoyé (add-in)</Badge>;
  if (item.row.direction === 'inbound_addin') return <Badge variant="success">Greffé (add-in)</Badge>;
  return <Badge variant="warning">Copie d'envoi</Badge>;
}

function emailLabel(item: MergedEmail): { title: string; subject: string } {
  if (item.kind === 'gmail') {
    return {
      title: item.row.from_name ?? item.row.from_email,
      subject: item.row.subject ?? '(sans objet)',
    };
  }
  return {
    title: item.row.from_email,
    subject: item.row.subject ?? '(sans objet)',
  };
}

export function DossierEmailsTab({
  dossierId,
  embedded = false,
}: {
  dossierId: string;
  embedded?: boolean;
}) {
  const { data: dossier } = useDossier(dossierId);
  const { data: gmailEmails, isLoading: isLoadingGmail } = useDossierEmails(dossierId);
  const { data: graftEmails, isLoading: isLoadingGraft } = useDossierGraftEmails(dossierId);
  const [selected, setSelected] = useState<MergedEmail | null>(null);

  const merged = useMemo<MergedEmail[]>(() => {
    const gmail: MergedEmail[] = (gmailEmails ?? []).map((row) => ({ kind: 'gmail', sentAt: row.sent_at, row }));
    const graft: MergedEmail[] = (graftEmails ?? []).map((row) => ({
      kind: 'graft',
      sentAt: row.sent_at ?? row.created_at,
      row,
    }));
    return [...gmail, ...graft].sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
  }, [gmailEmails, graftEmails]);

  const isLoading = isLoadingGmail || isLoadingGraft;

  return (
    <div className={embedded ? 'px-3 py-3 space-y-3' : 'p-4 space-y-4'}>
      {isLoading ? (
        <div className="p-4 text-sm text-neutral-500">Chargement…</div>
      ) : merged.length === 0 ? (
        <div className="p-8 text-center text-sm text-neutral-500">
          {`Aucun mail pour ce dossier. Transférez un mail à ${GRAFT_UNIFIED_ADDRESS} avec la référence ${dossier?.number ?? ''} dans le sujet.`}
        </div>
      ) : (
        <div className="space-y-2">
          {merged.map((item) => {
            const { title, subject } = emailLabel(item);
            return (
              <div key={`${item.kind}-${item.row.id}`} className="flex items-stretch gap-2">
                <button
                  onClick={() => setSelected(item)}
                  className="flex-1 min-w-0 text-left border rounded-lg p-3 bg-white hover:bg-gray-50 transition"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <EmailBadge item={item} />
                      <div className="text-sm font-medium text-neutral-900 truncate">{title}</div>
                    </div>
                    <div className="text-xs text-neutral-500 flex-shrink-0">
                      {formatAppDate(item.sentAt)}
                    </div>
                  </div>
                  <div className="text-xs text-neutral-600 truncate mt-1">{subject}</div>
                  {item.kind === 'gmail' && item.row.body_text && (
                    <div className="text-xs text-neutral-500 mt-1 line-clamp-2">{item.row.body_text.slice(0, 200)}</div>
                  )}
                </button>
                <div className="flex flex-col justify-center gap-1">
                  {canOpenInOutlook(item) ? (
                    <button
                      title="Ouvrir dans Outlook"
                      onClick={() => openGraftInOutlook(item.row)}
                      className="flex items-center border rounded-lg px-3 py-1.5 bg-white text-gray-500 hover:text-cime-primary hover:bg-gray-50 transition cursor-pointer"
                    >
                      <MailOpen className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      title="Télécharger .eml"
                      onClick={() => {
                        toast.promise(downloadEmailAsEml(item, dossierId), {
                          loading: 'Préparation du fichier…',
                          success: 'Fichier téléchargé — ouvrez-le pour répondre dans Outlook.',
                          error: 'Impossible de générer le fichier.',
                        });
                      }}
                      className="flex items-center border rounded-lg px-3 py-1.5 bg-white text-gray-500 hover:text-cime-primary hover:bg-gray-50 transition cursor-pointer"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    title="Réponse rapide (sans mise en forme)"
                    onClick={() => { window.location.href = buildReplyMailto(item.row); }}
                    className="flex items-center border rounded-lg px-3 py-1.5 bg-white text-gray-400 hover:text-cime-primary hover:bg-gray-50 transition cursor-pointer"
                  >
                    <Reply className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <DossierEmailViewerModal email={selected} dossierId={dossierId} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
